import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useGetConsignmentLinesQuery,
  type ConsignmentStatusCode,
  useCreatePallet,
  useCreatePalletBulk
} from '@/entities/consignments';
import type { PalletType } from '@/entities/consignments/api/palletsApi';
import { useProjectSiteParams } from '@/shared/lib';
import { calculateSelectedLinesInfo } from '@/features/pallet-view/lib/calculate-units';
import type { LocalPallet } from '@/features/pallet-view/model/types';
import type { ErrorResponse } from '@/shared/api/types';

interface UseCreatePalletModalProps {
  consignmentId: string;
  consignmentStatus?: ConsignmentStatusCode;
  selectedLineIds?: string[];
  onSuccess?: (pallets: LocalPallet[]) => void;
  onClose: () => void;
}

export interface CreatePalletFormData {
  type: PalletType;
  inventoryUnitsPerTransport: number;
  inventoryUnitType: string;
  transportUnitQuantity: number;
}

export function useCreatePalletModal({
  consignmentId,
  selectedLineIds: initialSelectedLineIds = [],
  onSuccess,
  onClose
}: UseCreatePalletModalProps) {
  const { t } = useTranslation();
  const { projectId, siteId } = useProjectSiteParams();

  // State
  const [selectedLineIds, setSelectedLineIds] = useState<string[]>(initialSelectedLineIds);
  const [error, setError] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);

  // API hooks with error handling
  const { createPallet } = useCreatePallet({
    onError: (err) => {
      const apiError = err as { data?: ErrorResponse };
      setError(apiError.data?.message || t('pallet.create.error.failed', 'Failed to create pallet'));
    }
  });

  const { createPalletBulk } = useCreatePalletBulk({
    onError: (err) => {
      const apiError = err as { data?: ErrorResponse };
      setError(apiError.data?.message || t('pallet.create.error.failed', 'Failed to create pallets'));
    }
  });

  // Fetch consignment lines
  const { data: linesData, isLoading } = useGetConsignmentLinesQuery(
    { consignmentId, projectId, siteId },
    { skip: !consignmentId || !projectId || !siteId }
  );

  const lines = useMemo(() => linesData?.items ?? [], [linesData]);

  // Calculate selected lines info
  const selectedLinesInfo = useMemo(() => calculateSelectedLinesInfo(selectedLineIds, lines), [selectedLineIds, lines]);

  // Update selectedLineIds when prop changes
  useEffect(() => {
    setSelectedLineIds(initialSelectedLineIds);
  }, [initialSelectedLineIds]);

  // Handle modal close
  const handleClose = useCallback(() => {
    setSelectedLineIds(initialSelectedLineIds);
    setError('');
    onClose();
  }, [onClose, initialSelectedLineIds]);

  // Handle form submission
  const handleSubmit = useCallback(
    async (formData: CreatePalletFormData) => {
      setError('');
      setIsProcessing(true);

      try {
        const hasLines = selectedLineIds.length > 0;

        if (!hasLines) {
          // Create a single empty pallet
          const result = await createPallet(
            {
              consignmentId,
              projectId,
              siteId
            },
            {
              type: formData.type,
              inventoryUnit: formData.inventoryUnitType || undefined
            }
          );

          // Convert API response to LocalPallet format for compatibility
          const localPallet: LocalPallet = {
            id: result.id.toString(),
            palletNumber: result.palletNumber,
            type: result.type as PalletType,
            assignedLineIds: []
          };

          onSuccess?.([localPallet]);
        } else {
          // Create one or more pallets and let the backend distribute (and split)
          // the selected lines across them. Works for quantity === 1 too.
          const results = await createPalletBulk(
            {
              consignmentId,
              projectId,
              siteId
            },
            {
              type: formData.type,
              quantity: formData.transportUnitQuantity,
              inventoryUnit: formData.inventoryUnitType,
              inventoryUnitsPerTransport: formData.inventoryUnitsPerTransport,
              lineIds: selectedLineIds,
              splitLines: true
            }
          );

          // Convert API response to LocalPallet format. Backend handles line
          // assignment/splitting; the view refreshes from the invalidated query.
          const localPallets: LocalPallet[] = results.map((p) => ({
            id: p.id.toString(),
            palletNumber: p.palletNumber,
            type: p.type as PalletType,
            assignedLineIds: []
          }));

          onSuccess?.(localPallets);
        }

        handleClose();
      } catch (err) {
        // Error is already handled by the hook's onError callback
        console.error('Error creating pallet:', err);
      } finally {
        setIsProcessing(false);
      }
    },
    [selectedLineIds, consignmentId, projectId, siteId, createPallet, createPalletBulk, onSuccess, handleClose]
  );

  return {
    // Data
    lines,
    selectedLineIds,
    selectedLinesInfo,

    // State
    isLoading,
    isProcessing,
    error,

    // Actions
    setSelectedLineIds,
    handleSubmit,
    handleClose
  };
}
